import { useEffect, useRef, useState } from 'react';
import { useRetailerSettings } from '../../hooks/useRetailerSettings';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import DOMPurify, { type Config } from 'dompurify';

// DOMPurify config: Allow most HTML/CSS but block dangerous scripts and event handlers
const HEADER_PURIFY_CONFIG: Config = {
  ADD_ATTR: ['target', 'class', 'style', 'type', 'href', 'rel', 'alt', 'title'],
  ADD_TAGS: ['style'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORCE_BODY: true,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: [
    'onerror',
    'onload',
    'onclick',
    'onmouseover',
    'onmouseout',
    'onmousemove',
    'onmouseenter',
    'onmouseleave',
  ],
  SAFE_FOR_TEMPLATES: true,
};

const renderHeaderHtml = (shadowRoot: ShadowRoot, html: string) => {
  const containerDiv = document.createElement('div');
  containerDiv.setAttribute('class', 'header-html-container');
  // Sanitize HTML to prevent XSS attacks
  containerDiv.innerHTML = DOMPurify.sanitize(html, HEADER_PURIFY_CONFIG);

  const existingContainer = shadowRoot.querySelector('.header-html-container');
  if (existingContainer) {
    existingContainer.remove();
  }

  shadowRoot.appendChild(containerDiv);
};

export const BrandedHeader = () => {
  const { features } = useRetailerSettings();

  const headerHtml = features?.headerHtml;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (shadowRoot) {
      // If headerHtml exists, render it
      let htmlToRender = '';
      if (headerHtml) {
        if (typeof headerHtml === 'string') {
          htmlToRender = headerHtml;
        } else if (
          typeof headerHtml === 'object' &&
          headerHtml !== null &&
          ('BASE' in headerHtml ||
            'DESKTOP' in headerHtml ||
            'MOBILE' in headerHtml)
        ) {
          const htmlObj = headerHtml as {
            BASE?: string;
            DESKTOP?: string;
            MOBILE?: string;
          };
          if (isMobile) {
            htmlToRender = htmlObj.MOBILE || htmlObj.BASE || '';
          } else {
            htmlToRender = htmlObj.DESKTOP || htmlObj.BASE || '';
          }
        }
        if (htmlToRender) {
          renderHeaderHtml(shadowRoot, htmlToRender);
        }
      }
    } else if (hostRef.current) {
      const root = hostRef.current.attachShadow({ mode: 'open' });
      setShadowRoot(root);
    }
  }, [shadowRoot, headerHtml, isMobile]);

  if (!headerHtml) return null;

  return <div ref={hostRef} data-testid="branded-header-host" />;
};
